/* eslint-disable react/no-array-index-key */
/* eslint-disable camelcase */
import { Table, Text } from '@lmc/ui-kit'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Carer,
  SkillNeedTemplate,
  SkillNeedTemplate_TrainingMatrix_DueAndOverdue_ByCarer_Response,
  SkillNeedTemplate_TrainingMatrix_dueAndOverdue_ByTraining_Response,
} from '@lmc/ts-common'
import styled from 'styled-components'
import { useQuery } from 'react-query'
import { getJSON } from 'common/services/api'
import { useSelector } from 'react-redux'
import {
  getDueOverDueByCarerRows,
  getDueOverDueByTrainingRows,
} from '../utils/tableUtils'
import ResidentCell, {
  ResidentCellProps,
} from '@lmc/ui-kit/src/molecules/Table/cells/ResidentCell'

const GET_DUE_OVERDUE_URL = `/api/list/skill-need-templates/training-matrix/due-and-overdue`

type ByCarerTableRecord = {
  teamMember: ResidentCellProps
  trainings: (string | undefined)[]
  dates: (string | undefined)[]
}
type ByTrainingTableRecord = {
  teamMembers: ResidentCellProps[]
  training: string | undefined
  dates: string[]
}

export type SkillNeedTemplateResponse = Pick<
  SkillNeedTemplate,
  'id' | 'trainingName' | 'trainingValidFor'
>

export type DueOverdueByCarerResponse =
  SkillNeedTemplate_TrainingMatrix_DueAndOverdue_ByCarer_Response

export type DueOverdueByTrainingResponse =
  SkillNeedTemplate_TrainingMatrix_dueAndOverdue_ByTraining_Response

const RESIDENT_CELL_MARGIN_TOP = 8
const RESIDENT_CELL_MARGIN_BOTTOM = 8
const ResidentCellStyled = styled(ResidentCell)`
  margin-top: ${RESIDENT_CELL_MARGIN_TOP}px;
  margin-bottom: ${RESIDENT_CELL_MARGIN_BOTTOM}px;
`

const TableStyled = styled(Table)`
  margin-top: 16px;
  margin-bottom: 32px;

  max-width: 100%;
  overflow-x: auto;
` as typeof Table

const renderReviewalDate = (date: string, t: (value: string) => string) => {
  if (date) {
    return (
      <Text style={{ fontSize: '14px' }} as="p1">
        {date}
      </Text>
    )
  }

  return (
    <Text style={{ fontWeight: 700 }} as="p1">
      {t('skillsAndNeeds.dots.achievedDateNotSet')}
    </Text>
  )
}
const AVATAR_SIZE = 30
const DateWrapper = styled.div`
  display: flex;
  align-items: center;

  height: ${AVATAR_SIZE}px; // height of ResidentCell
  margin-top: ${RESIDENT_CELL_MARGIN_TOP}px;
  margin-bottom: ${RESIDENT_CELL_MARGIN_BOTTOM}px;
`

const DueOverDueTrainingMatrix: FC = () => {
  const { t } = useTranslation()

  const { data: dueOverDueByCarerData, isLoading: dueOverDueByCarerLoading } =
    useQuery<{
      result: DueOverdueByCarerResponse
    }>('dueOverDueByCarer', () =>
      getJSON({ url: `${GET_DUE_OVERDUE_URL}?by=carer` })
    )

  const dueOverDueByCarer = dueOverDueByCarerData?.result

  const {
    data: dueOverDueByTrainingData,
    isLoading: dueOverDueByTrainingLoading,
  } = useQuery<{
    result: DueOverdueByTrainingResponse
  }>('dueOverDueByTraining', () =>
    getJSON({ url: `${GET_DUE_OVERDUE_URL}?by=training` })
  )

  const dueOverDueByTraining = dueOverDueByTrainingData?.result

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  return (
    <>
      <Text as="h3">{t(`skillsAndNeeds.dueAndOverdueTraining`)}</Text>
      <Text as="p1">{t(`skillsAndNeeds.byTeamMember`)}</Text>
      <TableStyled<ByCarerTableRecord>
        loading={dueOverDueByCarerLoading}
        pagination={false}
        columns={[
          {
            dataIndex: 'teamMember',
            key: 'teamMember',
            title: t(`skillsAndNeeds.table.teamMember`),
            render: (value: ResidentCellProps) => (
              <ResidentCell {...value} avatarSize={AVATAR_SIZE} />
            ),
          },
          {
            dataIndex: 'trainings',
            key: 'trainings',
            title: t(`skillsAndNeeds.table.training`),
            render: (trainings: string[]) =>
              trainings.map((trainingName) => (
                <div key={trainingName}>
                  <Text style={{ fontSize: '14px' }} as="p1">
                    {trainingName}
                  </Text>
                </div>
              )),
            width: 300,
          },
          {
            dataIndex: 'dates',
            key: 'dates',
            render: (dates: string[], record) =>
              dates.map((date, index) => (
                <div key={`Date-${date}-${record.teamMember}-${index}`}>
                  {renderReviewalDate(date, t)}
                </div>
              )),
            title: t(`skillsAndNeeds.table.reviewalDate`),
            width: 300,
          },
        ]}
        dataSource={getDueOverDueByCarerRows(dueOverDueByCarer, carers)}
      />
      <Text as="p1">{t(`skillsAndNeeds.byTraining`)}</Text>
      <TableStyled<ByTrainingTableRecord>
        pagination={false}
        loading={dueOverDueByTrainingLoading}
        columns={[
          {
            dataIndex: 'training',
            title: t(`skillsAndNeeds.table.training`),
            key: 'training',
          },
          {
            key: 'teamMembers',
            dataIndex: 'teamMembers',
            title: t(`skillsAndNeeds.table.teamMember`),
            render: (carers: ResidentCellProps[]) =>
              carers.map((carer, index) => (
                <ResidentCellStyled
                  // eslint-disable-next-line react/no-array-index-key
                  key={`resident-cell: ${carer.resident.name}${index}`}
                  {...carer}
                  avatarSize={AVATAR_SIZE}
                />
              )),
            width: 300,
          },
          {
            dataIndex: 'dates',
            render: (dates: string[], record) =>
              dates.map((date, index) => (
                <DateWrapper key={`Date-${date}-${record.training}-${index}`}>
                  {renderReviewalDate(date, t)}
                </DateWrapper>
              )),
            title: t(`skillsAndNeeds.table.reviewalDate`),
            width: 300,
          },
        ]}
        dataSource={getDueOverDueByTrainingRows(dueOverDueByTraining, carers)}
      />
    </>
  )
}

export default DueOverDueTrainingMatrix
